import { lazy } from 'react';
import { Route, Routes } from 'react-router-dom';

import Loadable from 'components/Loadable';
import { ProtectedRoute } from 'components/ProtectedRoute';
import { PublicRoute } from 'components/PublicRoute';
import Logout from 'components/SignOutButton';
import { DocusignTemplatesContextProvider } from './hooks/useDocusignTemplates';
import { SettingsContextProvider } from './hooks/useSettings';

const Home = Loadable(lazy(() => import('./pages/home/Home')));
const Users = Loadable(lazy(() => import('./pages/users/Users')));
const UserPage = Loadable(lazy(() => import('./pages/users/UserPage')));
const PipedriveEmbeddedDealManagement = Loadable(
  lazy(() => import('./pages/users/PipedriveEmbeddedDealManagement'))
);
const ReferralEarnings = Loadable(lazy(() => import('./pages/referrals/ReferralEarnings')));
const ReferralEarningsReviewRequests = Loadable(
  lazy(() => import('./pages/referrals/ReferralEarningsReviewRequests'))
);
const PassportRequests = Loadable(lazy(() => import('./pages/passport/PassportRequests')));
const PassportOnboardings = Loadable(
  lazy(() => import('./pages/passportOnboarding/PassportOnboardings'))
);
const PendingSmartfxLeads = Loadable(
  lazy(() => import('./pages/pendingSmartfxLeads/PendingSmartfxLeads'))
);
const SubmittedSmartfxLeads = Loadable(
  lazy(() => import('./pages/submittedSmartfxLeads/SubmittedSmartfxLeads'))
);
const PipedriveDuplicates = Loadable(
  lazy(() => import('./pages/pipedriveduplicates/PipedriveDuplicates'))
);
const FailedSmartfxLeads = Loadable(
  lazy(() => import('./pages/failedSmartfxLeads/FailedSmartfxLeads'))
);
const PartnerCompanies = Loadable(lazy(() => import('./pages/partner/PartnerCompanies')));
const Banks = Loadable(lazy(() => import('./pages/banks/Banks')));
const Utm = Loadable(lazy(() => import('./pages/utm/Utm')));
const PropertyFinderSettings = Loadable(
  lazy(() => import('./pages/propertyFinder/PropertyFinderSettings'))
);
const PipelineSettings = Loadable(lazy(() => import('./pages/pipelineSettings/PipelineSettings')));
const PipelineAssignments = Loadable(
  lazy(() => import('./pages/pipelineSettings/PipelineAssignments'))
);
const OwnerCalendlyUrls = Loadable(
  lazy(() => import('./pages/pipelineSettings/OwnerCalendlyUrls'))
);
const RemovedUsers = Loadable(lazy(() => import('./pages/removedUsers/RemovedUsers')));
const PartnerCompanyClients = Loadable(lazy(() => import('./pages/partner/PartnerCompanyClients')));
const PartnerClientManagement = Loadable(
  lazy(() => import('./pages/partner/PartnerClientManagement'))
);
const PartnerEarnings = Loadable(lazy(() => import('./pages/partnerEarnings/PartnerEarnings')));
const Login = Loadable(lazy(() => import('./pages/account/Login')));
const LoginPipedrive = Loadable(lazy(() => import('./pages/account/LoginPipedrive')));
const PasswordRecover = Loadable(lazy(() => import('./pages/account/PasswordRecover')));
const PasswordRecoverConfirm = Loadable(
  lazy(() => import('./pages/account/PasswordRecoverConfirm'))
);
const NotFound = Loadable(lazy(() => import('./pages/common/NotFound')));
const SellerCases = Loadable(lazy(() => import('./pages/sellerCases/SellerCases')));
const SellerCaseDetail = Loadable(lazy(() => import('./pages/sellerCases/SellerCaseDetail')));
const PipedriveEmbeddedSellerCase = Loadable(
  lazy(() => import('./pages/sellerCases/PipedriveEmbeddedSellerCase'))
);
const LoginPipedriveSellerCases = Loadable(
  lazy(() => import('./pages/account/LoginPipedriveSellerCases'))
);

const RoutesComponent = () => {
  return (
    <Routes>
      <Route
        path="/"
        element={
          <ProtectedRoute>
            <Home />
          </ProtectedRoute>
        }
      />
      <Route
        path="/users"
        element={
          <ProtectedRoute>
            <Users />
          </ProtectedRoute>
        }
      />
      <Route
        path="/admins"
        element={
          <ProtectedRoute>
            <Users />
          </ProtectedRoute>
        }
      />
      <Route
        path="/removed-users"
        element={
          <ProtectedRoute>
            <RemovedUsers />
          </ProtectedRoute>
        }
      />
      <Route
        path="/users/:email"
        element={
          <ProtectedRoute>
            <DocusignTemplatesContextProvider>
              <SettingsContextProvider>
                <UserPage />
              </SettingsContextProvider>
            </DocusignTemplatesContextProvider>
          </ProtectedRoute>
        }
      />
      <Route
        path="/pipedrive"
        element={
          <ProtectedRoute sidebar={false} pipedrive={true}>
            <SettingsContextProvider>
              <PipedriveEmbeddedDealManagement />
            </SettingsContextProvider>
          </ProtectedRoute>
        }
      />
      <Route
        path="/referrals/earnings"
        element={
          <ProtectedRoute>
            <ReferralEarnings />
          </ProtectedRoute>
        }
      />
      <Route
        path="/referrals/earnings/review-requests"
        element={
          <ProtectedRoute>
            <ReferralEarningsReviewRequests />
          </ProtectedRoute>
        }
      />
      <Route
        path="/passport-requests"
        element={
          <ProtectedRoute>
            <PassportRequests />
          </ProtectedRoute>
        }
      />
      <Route
        path="/passport-onboardings"
        element={
          <ProtectedRoute>
            <PassportOnboardings />
          </ProtectedRoute>
        }
      />
      <Route
        path="/smartfx"
        element={
          <ProtectedRoute>
            <SubmittedSmartfxLeads />
          </ProtectedRoute>
        }
      />
      <Route
        path="/smartfx-pending"
        element={
          <ProtectedRoute>
            <PendingSmartfxLeads />
          </ProtectedRoute>
        }
      />
      <Route
        path="/smartfx-failed"
        element={
          <ProtectedRoute>
            <FailedSmartfxLeads />
          </ProtectedRoute>
        }
      />
      <Route
        path="/partner-companies"
        element={
          <ProtectedRoute>
            <PartnerCompanies />
          </ProtectedRoute>
        }
      />
      <Route
        path="/partner-companies/search/:companyId"
        element={
          <ProtectedRoute>
            <PartnerCompanies />
          </ProtectedRoute>
        }
      />
      <Route
        path="/partner-companies/:companyId"
        element={
          <ProtectedRoute>
            <PartnerCompanyClients />
          </ProtectedRoute>
        }
      />
      <Route
        path="/partner-companies/:companyId/:email"
        element={
          <ProtectedRoute>
            <PartnerClientManagement />
          </ProtectedRoute>
        }
      />
      <Route
        path="/partner-earnings"
        element={
          <ProtectedRoute>
            <PartnerEarnings />
          </ProtectedRoute>
        }
      />
      <Route
        path="/banks"
        element={
          <ProtectedRoute>
            <Banks />
          </ProtectedRoute>
        }
      />
      <Route
        path="/utm"
        element={
          <ProtectedRoute>
            <Utm />
          </ProtectedRoute>
        }
      />
      <Route
        path="/property-finder/settings"
        element={
          <ProtectedRoute>
            <PropertyFinderSettings />
          </ProtectedRoute>
        }
      />
      <Route
        path="/pipeline-settings"
        element={
          <ProtectedRoute>
            <PipelineSettings />
          </ProtectedRoute>
        }
      >
        <Route path="assignments" element={<PipelineAssignments />} />
        <Route path="owner-urls" element={<OwnerCalendlyUrls />} />
        <Route index element={<PipelineAssignments />} />
      </Route>
      <Route
        path="/pipedrive-duplicates"
        element={
          <ProtectedRoute>
            <PipedriveDuplicates />
          </ProtectedRoute>
        }
      />
      <Route
        path="/seller-cases"
        element={
          <ProtectedRoute>
            <SellerCases />
          </ProtectedRoute>
        }
      />
      <Route
        path="/seller-cases/:caseId"
        element={
          <ProtectedRoute>
            <SettingsContextProvider>
              <SellerCaseDetail />
            </SettingsContextProvider>
          </ProtectedRoute>
        }
      />
      <Route
        path="/pipedrive-seller-cases"
        element={
          <ProtectedRoute sidebar={false} pipedrive={true}>
            <SettingsContextProvider>
              <PipedriveEmbeddedSellerCase />
            </SettingsContextProvider>
          </ProtectedRoute>
        }
      />
      <Route
        path="/passport-requests/:email"
        element={
          <ProtectedRoute>
            <PassportRequests />
          </ProtectedRoute>
        }
      />
      <Route
        path="/passport-onboardings/:email"
        element={
          <ProtectedRoute>
            <PassportOnboardings />
          </ProtectedRoute>
        }
      />
      <Route
        path="/login"
        element={
          <PublicRoute>
            <Login />
          </PublicRoute>
        }
      />
      <Route path="/login-pipedrive" element={<LoginPipedrive />} />
      <Route path="/login-pipedrive-seller-cases" element={<LoginPipedriveSellerCases />} />
      <Route
        path="/login/:email"
        element={
          <PublicRoute>
            <Login />
          </PublicRoute>
        }
      />
      <Route
        path="/password/recover"
        element={
          <PublicRoute>
            <PasswordRecover />
          </PublicRoute>
        }
      />
      <Route
        path="/password/recover/confirm/:email"
        element={
          <PublicRoute>
            <PasswordRecoverConfirm />
          </PublicRoute>
        }
      />
      <Route
        path="/logout"
        element={
          <ProtectedRoute>
            <Logout />
          </ProtectedRoute>
        }
      />
      <Route
        path="*"
        element={
          <PublicRoute>
            <NotFound />
          </PublicRoute>
        }
      />
    </Routes>
  );
};

export default RoutesComponent;
